import React from 'react';
import { useDraggable } from '@dnd-kit/core';

import { columnsOf } from '../../config/repeatingGroups';
import { elementLabel } from '../items/itemValueDisplay';
import { cellBox, groupDragId, lineTopsOf, lostColumns, readableGroup } from './pageGroupLines';

/**
 * A group block's lines and handles in the page designer (KPR-1863, KPE-041), in the
 * layer that has the block's own box (BlockHandles), so none of it prints.
 *
 * The lines and the columns' edges are always drawn: they are what the person lines
 * up with the form. With the block selected:
 *   - a tab above the first line for each chosen column, dragged to move it, and by
 *     its right edge to size it;
 *   - a dot on each line after the first, dragged to move that line alone;
 *   - a tab at the foot that adds or takes off whole lines;
 *   - a dot at the right end of the first line, dragged up or down to meet a form
 *     that is not square on the page.
 *
 * Each is a dnd-kit draggable, so Space, the arrows and Space carry it too. None of
 * them follows `transform`: the drag redraws the block from a preview of the model
 * (PageLayoutEditorPage), and the handle moves with what it moves. Keys are stable,
 * because dnd-kit's listeners sit on the element the press began on (never remounted).
 *
 * THE LINE DOTS HAVE A PARENT OF THEIR OWN, AND IT MUST STAY SO. dnd-kit measures the
 * carried handle again whenever a child is added to or taken from anything that holds
 * it, and takes how far it moved off `delta`. Adding a line mounts a dot: beside the
 * Lines tab, that re-measure took the line back off the delta, which took the dot
 * away, which put the delta back, for as long as the pointer was held.
 *
 * A column this block cannot print is never named: it may be hidden from this person
 * (pageGroupLines.lostColumns), and the name in the layout is not theirs to read.
 */

function Handle({ id, className, style, label, children = null }) {
	const { attributes, listeners, setNodeRef } = useDraggable({ id });
	return (
		<span ref={setNodeRef} className={className} style={style} aria-label={label} {...attributes} {...listeners}>
			{children}
		</span>
	);
}

/** The lines and column edges, in the block's own units, so no number here is in pixels. */
function Guides({ block }) {
	const tops = lineTopsOf(block);
	const { w, h } = block;
	const slope = (block.tilt || 0) / w;
	const across = top => ({ x1: -slope * top, y1: top, x2: w - slope * top, y2: top + slope * w });
	const down = x => ({ x1: x, y1: slope * x, x2: x - slope * h, y2: h + slope * x });
	const edges = [...new Set((block.columns || []).flatMap(column => [column.x, column.x + column.w]))];
	return (
		<svg className="page-group__lines" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true" data-testid="group-lines">
			{[...tops, h].map((top, index) => <line key={`l${index}`} {...across(top)} vectorEffect="non-scaling-stroke" />)}
			{edges.map(x => <line key={`c${x}`} className="page-group__edge" {...down(x)} vectorEffect="non-scaling-stroke" />)}
		</svg>
	);
}

export default function PageGroupHandles({ block, group, page, sheet = page, selected }) {
	const { unit } = page;
	const tops = lineTopsOf(block);
	const inUnits = value => `${value}${unit}`;
	const readable = readableGroup(group);
	const lost = new Set(readable ? lostColumns(block, readable) : (block.columns || []).map(column => column.name));
	const labelOf = (name) => {
		const column = columnsOf(readable).find(candidate => candidate?.name === name);
		return elementLabel(column) || name;
	};
	const slope = (block.tilt || 0) / block.w;
	// Above the first line, or at the sheet's top edge: the sheet clips what is past it.
	const above = top => `max(calc(${inUnits(top)} - var(--group-tab, 22px)), ${inUnits(-block.y)})`;
	return (
		<>
			<Guides block={block} />
			{lost.size || !readable ? <span className="page-group__flag">Check its columns</span> : null}
			{selected ? (
				<>
					{(block.columns || []).map((column, index) => {
						const box = cellBox(block, 0, column, tops);
						const gone = lost.has(column.name);
						const name = gone ? `${index + 1}, not available` : labelOf(column.name);
						return (
							<React.Fragment key={column.name}>
								<Handle
									id={groupDragId('col', block.id, column.name)}
									className={`page-group__tab${gone ? ' page-group__tab--lost' : ''}`}
									style={{ left: inUnits(box.left), top: above(box.top), width: inUnits(column.w) }}
									label={`Move column ${name}`}
								>
									<span className="page-group__tab-name">{gone ? 'Not available' : name}</span>
								</Handle>
								<Handle
									id={groupDragId('colw', block.id, column.name)}
									className="page-group__tab-edge"
									style={{ left: `calc(${inUnits(box.left + column.w)} - 6px)`, top: above(box.top) }}
									label={`Size column ${name}`}
								/>
							</React.Fragment>
						);
					})}
					<span className="page-group__dots">
						{tops.slice(1).map((top, index) => {
							const line = index + 1;
							return (
								<Handle
									key={`line${line}`}
									id={groupDragId('line', block.id, line)}
									className="page-group__dot"
									style={{ left: inUnits(-slope * top), top: inUnits(top) }}
									label={`Move line ${line + 1}`}
								/>
							);
						})}
					</span>
					{/* Just inside the right end, over the last column's edge, which is the other way to reach it. */}
					<Handle
						id={groupDragId('tilt', block.id)}
						className="page-group__dot page-group__dot--tilt"
						style={{ left: `calc(${inUnits(block.w)} - 8px)`, top: inUnits(block.tilt || 0) }}
						label="Tilt the lines to match the form"
					/>
					<Handle
						id={groupDragId('count', block.id)}
						className="page-group__count"
						// At the sheet's foot, an area can reach it and the sheet clips what is past it.
						style={{ top: `min(100%, calc(${inUnits(sheet.height - block.y)} - var(--group-count, 20px)))` }}
						label="Add or remove lines"
					>
						Lines
					</Handle>
				</>
			) : null}
		</>
	);
}
